import { lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { RoleGuard } from '@portals/shared/guards/RoleGuard'
import { OperationsShell } from './OperationsShell'
// KAN-1181: the control-tower shell stays eager (it hosts the routed <Outlet>); each ops
// board is code-split so it downloads only when that route is visited. These modules export
// named components, so we adapt them to the default export React.lazy expects.
const OperationsDashboard = lazy(() =>
  import('./OperationsDashboard').then(m => ({ default: m.OperationsDashboard }))
)
const OnboardingQueue = lazy(() =>
  import('./OnboardingQueue').then(m => ({ default: m.OnboardingQueue }))
)
const WorkflowBoard = lazy(() => import('./WorkflowBoard').then(m => ({ default: m.WorkflowBoard })))
const OpsWatchlistBoard = lazy(() =>
  import('./OpsWatchlistBoard').then(m => ({ default: m.OpsWatchlistBoard }))
)
import { PortalLoginPage } from '@portals/shared/components/PortalLoginPage'
import { ToastProvider } from '@/components/feedback/ToastProvider'
import { DialogProvider } from '@/components/ui'
import OtpSecuritySettings from '@components/security/OtpSecuritySettings'

export function OperationsPortal() {
  const renderOperationsLogin = () => (
    <PortalLoginPage
      portal="operations"
      title="Operations Control Tower Access"
      description={[
        'Manage onboarding approvals, workflow escalations, and service recovery tasks.',
        'Only Retail Managers, Customer Care leads, or Super Admins should sign in here.'
      ]}
      scope="global"
      requestedScopes={['accounts:approve', 'workflow:manage', 'orders:write']}
      redirectPath="/"
      allowedRoles={['RETAIL_MANAGER', 'CUSTOMER_CARE', 'SUPER_ADMIN']}
    />
  )

  return (
    <Router>
      <ToastProvider>
        <DialogProvider>
        <Routes>
          <Route
            path="/login"
            element={renderOperationsLogin()}
          />
        <Route
          path="/*"
          element={
            <RoleGuard
              portal="operations"
              allowedRoles={['RETAIL_MANAGER', 'CUSTOMER_CARE', 'SUPER_ADMIN']}
              requiredScopes={['accounts:approve', 'workflow:manage', 'orders:write']}
              fallback={renderOperationsLogin()}
            >
              <OperationsShell />
            </RoleGuard>
          }
        >
          <Route index element={<OperationsDashboard />} />
          {/* KAN-791: wired to the real admin-service ops aggregates (onboarding/workflow/delivery+returns). */}
          <Route path="onboarding" element={<OnboardingQueue />} />
          <Route path="workflow" element={<WorkflowBoard />} />
          <Route path="order-exceptions" element={<OpsWatchlistBoard />} />
          <Route
            path="account-security"
            element={<OtpSecuritySettings portal="operations" hideWhenVerified={false} className="operations-otp-settings" />}
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </DialogProvider>
      </ToastProvider>
    </Router>
  )
}
